import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { passwordResetCompleteSchema } from '@perplexicon/validation';
import type { PasswordResetComplete } from '@perplexicon/validation';

import { Button, Card, ErrorState, Input } from '../../components/index.js';
import { useCompletePasswordReset } from '../../lib/auth.js';
import { getErrorMessage } from '../../lib/errors.js';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const completeReset = useCompletePasswordReset();
  const {
    register: field,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetComplete>({
    resolver: zodResolver(passwordResetCompleteSchema),
    defaultValues: { token, newPassword: '' },
  });

  const onSubmit = handleSubmit((values) => {
    completeReset.mutate(values, {
      onSuccess: () => {
        void navigate('/login', { replace: true });
      },
    });
  });

  if (!token) {
    return (
      <Card variant="parchment">
        <ErrorState message="This password reset link is missing its token. Please request a new one." />
        <Link
          to="/forgot-password"
          className="mt-4 inline-block text-sm font-semibold text-link hover:text-linkHover"
        >
          Request a new link
        </Link>
      </Card>
    );
  }

  return (
    <Card variant="parchment">
      <h1 className="font-display text-2xl font-semibold text-textPrimary">
        Choose a new password
      </h1>
      <form onSubmit={(e) => void onSubmit(e)} noValidate className="mt-4 flex flex-col gap-4">
        <input type="hidden" {...field('token')} value={token} />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          hint="At least 12 characters."
          error={errors.newPassword?.message}
          {...field('newPassword')}
        />
        {completeReset.isError && <ErrorState message={getErrorMessage(completeReset.error)} />}
        <Button
          type="submit"
          emphasis="decorated"
          isLoading={completeReset.isPending}
          className="w-full"
        >
          Reset password
        </Button>
      </form>
    </Card>
  );
}
